
/* eslint-disable object-curly-newline */
/* global FormData */

import React, { useRef, useState, useEffect } from 'react'

import FileTransferRequests from '../../../../../components/file-transfer/utils/FileTransferRequests'
import { composeClassName } from '../../utils/common'
import { MessageTypes } from '../../constants/index'
import { FileMessage } from '../../models'
import CloseButton from '../../../../../components/chat-tools/CloseButton'
import RetryButton from '../../../../../components/chat-tools/RetryButton'
import RepickFileButton from '../../../../../components/chat-tools/RepickFileButton'
import { ErrorIcon } from '../../../../../components/icons/index'
import { MessageProps } from '../../../MessageDisplayArea'
import { CreateFileTransferExtraParamsOutput } from '../../../../chat-tools-area/utils/createFileTransferExtraParams'
import MessageTime from '../MessageTime'
import {
  CLOSE,
  FILE_SIZE_EXCEEDED,
  SEND_FILE_FAIL,
  TRANSFER_INTERRUPTED,
  TRANSFERRED,
} from '../../../../../constants/languages'

interface ErrorState {
  retryButtonEnabled: boolean;
  repickButtonEnabled: boolean;
  message: string;
}

interface FileData extends File {
  name: string;
  size: number;
}

export interface FileUploadMessageProps extends Omit<MessageProps, 'message'> {
  message: FileMessage;
  onRemove?: () => void;
  onRepick?: (filename: string, file: File) => void;
  submitExtraParams: CreateFileTransferExtraParamsOutput;
}

const MAX_FILE_SIZE = 100 * 1024 * 1024; /* 100MB */

const defaultErrorState: ErrorState = {
  retryButtonEnabled: false,
  repickButtonEnabled: false,
  message: '',
};

const FileUploadMessage: React.FC<FileUploadMessageProps> = props => {
  const {
    message: {
      file,
      filename,
      nickname,
      isFirstInGroup,
      isLastInGroup,
    },
    common: { texts },
    onRemove = () => {},
    onRepick = () => {},
    submitExtraParams,
  } = props
  const [uploaded, setUploaded] = useState<number>(0);
  const [request, setRequest] = useState<FileTransferRequests | null>(null);
  const [error, setError] = useState<ErrorState>(defaultErrorState);

  const onSetLoadStatus = (newUploaded: number | string): void => {
    setUploaded(Number(newUploaded));
  };

  const onEndLoading = (newResult: boolean): void => {
    if (!newResult) {
      setError({ message: texts[SEND_FILE_FAIL], retryButtonEnabled: true, repickButtonEnabled: false })
      return
    }
  };

  const createFileRequest = (fileToUpload: FileData): FileTransferRequests => {
    const formData = new FormData();
    formData.append('nickname', nickname as string);
    formData.append('sent_file', fileToUpload);

    return new FileTransferRequests({
      sendFileParams: formData,
      progressLoadedParams: submitExtraParams.progressSubmitParams,
      actionUrl: submitExtraParams.actionUrl,
      onStartLoading: () => {},
      onEndLoading,
      onSetLoadStatus,
      onError,
    });
  };

  const onError = (): void => {
    if (request) {
      request.abort();
    }
    onRemove();
  };

  const handleRepickFile = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const { files } = event.target;
    setError(prev => ({ ...prev, message: '' }));
    if (files && filename) {
      onRepick(filename, files[0]);
    }
  };

  const sendFile = () => {
    if (!file?.name) {
      setError({ message: texts[TRANSFER_INTERRUPTED], retryButtonEnabled: false, repickButtonEnabled: true })
      return
    }

    if (file?.size > MAX_FILE_SIZE) {
      setError({ message: texts[FILE_SIZE_EXCEEDED], retryButtonEnabled: false, repickButtonEnabled: false })
      return
    }

    setError(defaultErrorState)
    setUploaded(0)

    const newRequest = createFileRequest(file)
    setRequest(newRequest)
  };

  const prevFileRef = useRef<File | null>(file)
  const prevSessionIdRef = useRef<string | undefined>();
  useEffect(() => {
    const { fileTransferSessionId } = submitExtraParams.progressSubmitParams
    const sessionIdJustAppeared = !prevSessionIdRef.current && !!fileTransferSessionId
    const fileChanged = file && prevFileRef.current !== file
    if (sessionIdJustAppeared || fileChanged) {
      sendFile()
    }

    prevFileRef.current = file
    prevSessionIdRef.current = fileTransferSessionId;
  }, [file, submitExtraParams.progressSubmitParams.fileTransferSessionId])

  useEffect(() => {
    return () => {
      if (request) {
        request.unsubscribe();
      }
    };
  }, [request]);

  if (!file) return null;

  const fileSizeMB = file.size / (1024 * 1024);
  const fsize = fileSizeMB.toFixed(2);
  const fsizeNumber = fileSizeMB;
  const transferredText = `${file.name}: ${texts[TRANSFERRED]} ${uploaded}MB / ${fsize}MB.`;
  const className = composeClassName(MessageTypes.VISITOR, isFirstInGroup, Boolean(isLastInGroup));

  return (
    <div className={`${className}`}>
      {isFirstInGroup && (
        <>
          <p className="author">{nickname}</p>
          {<MessageTime {...props} />}
        </>
      )}

      <div className="file-upload-message message-text">
        <div className="file-message-content">
          <div className="file-upload-text">
            {error.message ? (
              <div className="file-error">
                <ErrorIcon />
                {error.message}
              </div>
            ) : (
              <span>{transferredText}</span>
            )}
          </div>

          <div className="buttons">
            {error.retryButtonEnabled && (
              <RetryButton
                onClick={sendFile}
                text={error.message}
              />
            )}
            {error.repickButtonEnabled && (
              <RepickFileButton
                onChange={handleRepickFile}
                text={error.message}
              />
            )}
            <CloseButton
              onCloseTool={onError}
              closeButtonText={texts[CLOSE] || ''}
            />
          </div>
        </div>

        <div className="progress-wrapper">
          <span
            className="progress-bar"
            style={{ width: `${uploaded / fsizeNumber * 100}%` }}
          />
        </div>
      </div>
    </div>
  );
};

export default FileUploadMessage
